import { useQuery } from '@tanstack/react-query';
import { useTransactionService } from '../../../services/transaction.service';
import { Transaction, TransactionsFilterData } from '../../../services/transaction.types';
import { useCallback, useMemo } from 'react';

export const useTransactionQueries = (
    page: number,
    rowsPerPage: number,
    order: 'asc' | 'desc',
    orderBy: string,
    filters: TransactionsFilterData,
    statusFilters?: string[]
) => {
    const transactionService = useTransactionService();

    // Handle single status filter through the normal API call
    const {
        data: singleFilterResponse,
        isLoading: isSingleFilterLoading,
        isFetching: isSingleFilterFetching,
    } = useQuery({
        queryKey: ['transactions', page, rowsPerPage, order, orderBy, filters],
        queryFn: () => transactionService.get(page, rowsPerPage, filters, order, orderBy),
        gcTime: 0,
        // Only enabled if there are 0 or 1 status filters (already handled in filters object)
        enabled: !statusFilters || statusFilters.length <= 1,
    });

    // For multiple status filters, make a separate query for each status and combine the results
    const getMultipleStatusResults = useCallback(async () => {
        // Don't run if we don't have multiple status filters
        if (!statusFilters || statusFilters.length <= 1) {
            return null;
        }
        
        // Create a copy of the base filters without reviewTypes
        const baseFilters = { ...filters };
        delete baseFilters.reviewTypes;
        
        // Make a separate query for each status filter
        const results = await Promise.all(
            statusFilters.map(status => {
                const statusFilter = { 
                    ...baseFilters, 
                    reviewTypes: [status] 
                };
                return transactionService.get(page, rowsPerPage, statusFilter, order, orderBy);
            })
        );
        
        // Combine and deduplicate results
        const combinedTransactions: Transaction[] = [];
        const transactionIds = new Set<number>();
        
        results.forEach(result => {
            if (result && result.data) {
                result.data.forEach((transaction: Transaction) => {
                    // Only add transactions we haven't seen before (deduplicate)
                    if (transaction.id !== undefined && !transactionIds.has(transaction.id)) {
                        transactionIds.add(transaction.id);
                        combinedTransactions.push(transaction);
                    }
                });
            }
        });
        
        // Return a combined result with all unique transactions
        return {
            data: combinedTransactions,
            meta: results[0]?.meta || { total: combinedTransactions.length, page, size: rowsPerPage }
        };
    }, [statusFilters, filters, page, rowsPerPage, order, orderBy, transactionService]);

    // Query for multiple status filters
    const {
        data: multiFilterResponse,
        isLoading: isMultiFilterLoading,
        isFetching: isMultiFilterFetching,
    } = useQuery({
        queryKey: ['transactions-multi-status', page, rowsPerPage, order, orderBy, statusFilters],
        queryFn: getMultipleStatusResults,
        gcTime: 0,
        // Only enabled if we have multiple status filters
        enabled: statusFilters && statusFilters.length > 1,
    });

    // Use the appropriate response based on whether we're using single or multiple status filters
    const transactionResponse = useMemo(() => {
        if (statusFilters && statusFilters.length > 1) {
            return multiFilterResponse;
        }
        return singleFilterResponse;
    }, [multiFilterResponse, singleFilterResponse, statusFilters]);

    const { data: transactionTypes = [] } = useQuery({
        queryKey: ['transactionTypes'],
        queryFn: () => transactionService.getTypes(),
    });

    // Combine loading states from both queries
    const isLoading = isSingleFilterLoading || isMultiFilterLoading;
    const isFetching = isSingleFilterFetching || isMultiFilterFetching;

    return {
        transactionResponse,
        transactionTypes,
        isLoading,
        isFetching,
    };
};
